import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { type FiltersFocusType, type SidebarsLockedType } from '@/defs/view'

type MenuStatusType = 'collapsed' | 'expanded'
type MenuStatusExtendedType = MenuStatusType | 'locked'
export type MenuStatusHoveredType = MenuStatusExtendedType | 'hovered'
type MobileStatus = 'closed' | 'nav' | 'clipboard' | 'filters'

export type ViewStateType = {
  clipboardPreload: boolean
  clipboardStatus: MenuStatusExtendedType
  filtersFocus: FiltersFocusType | null
  filtersStatus: MenuStatusHoveredType
  gallerySidebarStatus: MenuStatusType
  isRehydrated: boolean
  mobileStatus: MobileStatus
}

const initialState: ViewStateType = {
  clipboardPreload: false,
  clipboardStatus: 'collapsed',
  filtersFocus: null,
  filtersStatus: 'collapsed',
  gallerySidebarStatus: 'collapsed',
  isRehydrated: false,
  mobileStatus: 'closed',
}

export const getSidebars = (view: ViewStateType): SidebarsLockedType => ({
  clipboard: view.clipboardStatus === 'locked',
  filters: view.filtersStatus === 'locked',
  gallery: view.gallerySidebarStatus === 'expanded',
})

export const viewSlice = createSlice({
  initialState,
  name: 'view',
  reducers: {
    closeMobileMenus: (s) => {
      s.mobileStatus = 'closed'
    },
    collapseClipboard: (s) => {
      s.clipboardStatus = 'collapsed'
    },
    collapseFilters: (s) => {
      s.filtersStatus = 'collapsed'
    },
    collapseSidebars: (s) => {
      if (s.clipboardStatus !== 'locked') s.clipboardStatus = 'collapsed'
      if (s.filtersStatus !== 'locked') s.filtersStatus = 'collapsed'
    },
    expandFilters: (s) => {
      if (s.filtersStatus !== 'locked') s.filtersStatus = 'expanded'
    },
    hoverFilters: (s) => {
      if (s.filtersStatus === 'collapsed') s.filtersStatus = 'hovered'
    },
    initSidebars: (s, action: PayloadAction<SidebarsLockedType>) => {
      if (action.payload.clipboard) s.clipboardStatus = 'locked'
      if (action.payload.filters) s.filtersStatus = 'locked'
      if (action.payload.gallery) s.gallerySidebarStatus = 'expanded'
    },
    preloadClipboard: (s) => {
      s.clipboardPreload = true
    },
    setFiltersFocus: (s, action: PayloadAction<FiltersFocusType | null>) => {
      s.filtersFocus = action.payload
    },
    setRehydrated: (s) => {
      s.isRehydrated = true
    },
    toggleClipboard: (s) => {
      s.clipboardStatus = s.clipboardStatus === 'collapsed' ? 'expanded' : 'collapsed'
    },
    toggleClipboardLock: (s) => {
      s.clipboardStatus = s.clipboardStatus === 'locked' ? 'expanded' : 'locked'
    },
    toggleFilters: (s) => {
      s.filtersStatus = s.filtersStatus === 'collapsed' || s.filtersStatus === 'hovered' ? 'expanded' : 'collapsed'
    },
    toggleFiltersLock: (s) => {
      s.filtersStatus = s.filtersStatus === 'locked' ? 'expanded' : 'locked'
    },
    toggleGallerySidebar: (s) => {
      s.gallerySidebarStatus = s.gallerySidebarStatus === 'collapsed' ? 'expanded' : 'collapsed'
    },
    toggleMobileStatus: (s, action: PayloadAction<MobileStatus>) => {
      s.mobileStatus = s.mobileStatus === action.payload ? 'closed' : action.payload
    },
    unhoverFilters: (s) => {
      if (s.filtersStatus === 'hovered') s.filtersStatus = 'collapsed'
    },
  },
})

export const viewActions = viewSlice.actions
export const viewReducer = viewSlice.reducer
